import { Title } from '@solidjs/meta'
import { type Component, createResource, createSignal } from 'solid-js'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Chip from '~/components/Chip'
import Form from '~/components/Form'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import Select from '~/components/Select'
import Table from '~/components/Table'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import api from '~/services/api'

interface BuilderComponent {
  id: string
  name: string
  type: string
  props: Record<string, unknown>
  createdAt: string
}

const COMPONENT_TYPES = [
  { value: 'Button', title: 'Button' },
  { value: 'Table', title: 'Table' },
  { value: 'Form', title: 'Form' },
  { value: 'Container', title: 'Container' },
  { value: 'Card', title: 'Card' },
  { value: 'Input', title: 'Input' },
  { value: 'Modal', title: 'Modal' },
  { value: 'Badge', title: 'Badge' },
  { value: 'Icon', title: 'Icon' },
  { value: 'Text', title: 'Text' },
  { value: 'Image', title: 'Image' },
  { value: 'List', title: 'List' }
]

const BuilderComponents: Component = () => {
  const alert = useAlert()
  const [items, { refetch }] = createResource(async () => {
    try {
      const { data } = await api.get('/components')
      return (data?.data?.items ?? []) as BuilderComponent[]
    } catch {
      return []
    }
  })

  const [showModal, setShowModal] = createSignal(false)
  const [editing, setEditing] = createSignal<BuilderComponent | null>(null)
  const [isSubmitting, setIsSubmitting] = createSignal(false)
  const toast = useToast()

  const openCreate = () => {
    setEditing(null)
    setShowModal(true)
  }
  const openEdit = (c: BuilderComponent) => {
    setEditing(c)
    setShowModal(true)
  }

  const handleSubmit = async (formData: FormData) => {
    setIsSubmitting(true)
    try {
      const payload = { name: formData.get('name') as string, type: (formData.get('type') as string) || 'Button' }
      if (editing()) {
        await api.put(`/components/${editing()!.id}`, payload)
        toast.addToast({ title: 'Componente actualizado', type: 'success' })
      } else {
        await api.post('/components', payload)
        toast.addToast({ title: 'Componente creado', type: 'success' })
      }
      setShowModal(false)
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: err instanceof Error ? err.message : 'Error', type: 'danger' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar componente',
      message: '¿Estás seguro de eliminar este componente?'
    })
    if (!ok) return
    try {
      await api.delete(`/components/${id}`)
      toast.addToast({ title: 'Componente eliminado', type: 'success' })
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: err instanceof Error ? err.message : 'Error', type: 'danger' })
    }
  }

  const columns = [
    { header: 'Nombre', accessor: (c: BuilderComponent) => c.name },
    {
      header: 'Tipo',
      accessor: (c: BuilderComponent) => (
        <Chip variant="soft" size="sm">
          {c.type}
        </Chip>
      )
    }
  ]

  return (
    <>
      <Title>Builder - Componentes | Florez Jaramillo</Title>
      <Table<BuilderComponent>
        title="Componentes"
        subtitle="Componentes reutilizables para tus páginas"
        headerActions={
          <Button onClick={openCreate} prependIcon="material-symbols-light:add-circle-outline">
            Nuevo Componente
          </Button>
        }
        data={items() ?? []}
        columns={columns}
        keyField="id"
        loading={items.loading}
        searchable
        searchPlaceholder="Buscar componente..."
        emptyMessage="Aún no hay componentes"
        actions={c => (
          <ButtonGroup size="sm" variant="soft">
            <Button color="warning" onClick={() => openEdit(c)} tooltip="Editar" icon="material-symbols-light:edit-outline" />
            <Button color="danger" onClick={() => handleDelete(c.id)} tooltip="Eliminar" icon="material-symbols-light:delete-outline" />
          </ButtonGroup>
        )}
      />
      <Modal isOpen={showModal()} onClose={() => setShowModal(false)}>
        <Modal.Header>
          <h2 class="text-xl font-bold text-foreground">{editing() ? 'Editar Componente' : 'Nuevo Componente'}</h2>
        </Modal.Header>
        <Modal.Body>
          <Form id="component-form" onSubmit={handleSubmit} initialValues={editing() ? { name: editing()!.name, type: editing()!.type } : undefined}>
            <div class="space-y-4">
              <Input name="name" label="Nombre" required placeholder="ej: Mi Botón" />
              <Select name="type" label="Tipo" options={COMPONENT_TYPES} placeholder="Selecciona un tipo" />
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button type="button" variant="soft" color="default" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting()} form="component-form">
              {isSubmitting() ? 'Guardando...' : editing() ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <alert.AlertComponent />
    </>
  )
}

export default BuilderComponents
